/**
 * TrashLab Analytics Tracker
 *
 * A lightweight client-side script for tracking website analytics data.
 * This script collects user interactions and sends them to the TrashLab analytics API.
 */

import { vanillaTrpc } from '@trashlab/api/src/library/frontend/utils/trpc';
import { trpcWebsiteTrackAnalyticsEvent } from '@trashlab/api/src/generated/hooks';
import { DateTime } from 'luxon';

function vanillaTrackAnalyticsEventMutation(
  event: typeof trpcWebsiteTrackAnalyticsEvent.input,
): Promise<typeof trpcWebsiteTrackAnalyticsEvent.output> {
  return (
    (vanillaTrpc as any)['website.mutations.trackAnalyticsEvent'] as any
  ).mutate(event);
}

interface TrackingEvent {
  websiteId: string;
  eventType:
    | 'pageview'
    | 'conversion'
    | 'click'
    | 'form_submission'
    | 'session_start'
    | 'session_end'
    | 'form_field_change';
  path: string;
  referrer: string | null;
  userAgent: string;
  visitorId: string;
  sessionId: string;
  timestamp: DateTime;
  metadata: Record<string, any> | null;
  country?: string;
  region?: string;
  city?: string;
}

interface TrackerConfig {
  websiteId: string;
  apiBaseUrl: string;
  debug?: boolean;
  disableCookies?: boolean;
  excludePaths?: string[];
}

interface SessionData {
  sessionId: string;
  visitorId: string;
  startTime: number;
  lastActivity: number;
  pagesViewed: string[];
}

export class TrashLabAnalytics {
  private config: TrackerConfig;
  private session: SessionData | null = null;
  private eventQueue: TrackingEvent[] = [];
  private isProcessingQueue = false;
  private flushInterval: number = 5000; // 5 seconds
  private sessionTimeout: number = 30 * 60 * 1000; // 30 minutes
  private timer: ReturnType<typeof setInterval> | null = null;
  private pageStartTime: DateTime | null = null;
  private lastPagePath: string | null = null;

  constructor(config: TrackerConfig) {
    this.config = {
      debug: false,
      disableCookies: false,
      excludePaths: [],
      ...config,
    };

    // Initialize session
    this.initSession();

    // Set up event listeners
    this.setupEventListeners();

    // Start the queue processing timer
    this.startQueueTimer();

    // Track initial page view
    this.trackPageView();

    this.log('TrashLab Analytics initialized');
  }

  /**
   * Initialize the user session
   */
  private initSession() {
    if (this.config.disableCookies) {
      // Generate a new session for this page view only
      this.session = this.createNewSession();
      return;
    }

    // Try to load existing session from localStorage
    const savedSession = this.loadSession();

    if (savedSession) {
      const now = DateTime.now().toMillis();

      // Check if the session has timed out
      if (now - savedSession.lastActivity > this.sessionTimeout) {
        // Session expired, create a new one but keep the visitor ID
        this.session = this.createNewSession(savedSession.visitorId);
        this.log('Session expired, creating new session');
      } else {
        // Resume existing session
        this.session = savedSession;
        this.session.lastActivity = now;
        this.saveSession();
        this.log('Resuming existing session');
      }
    } else {
      // No existing session, create a new one
      this.session = this.createNewSession();
      this.log('Creating new session');
    }

    // Track session start if this is a new session
    if (
      !savedSession ||
      DateTime.now().toMillis() - savedSession.lastActivity >
        this.sessionTimeout
    ) {
      this.trackEvent({
        websiteId: this.config.websiteId,
        eventType: 'session_start',
        path: window.location.pathname,
        referrer: null,
        userAgent: navigator.userAgent,
        visitorId: this.session.visitorId,
        sessionId: this.session.sessionId,
        timestamp: DateTime.now(),
        metadata: null,
      });
    }
  }

  /**
   * Create a new session
   */
  private createNewSession(existingVisitorId?: string): SessionData {
    return {
      sessionId: this.generateId(),
      visitorId: existingVisitorId || this.generateId(),
      startTime: DateTime.now().toMillis(),
      lastActivity: DateTime.now().toMillis(),
      pagesViewed: [window.location.pathname],
    };
  }

  /**
   * Save the current session to localStorage
   */
  private saveSession() {
    if (this.config.disableCookies || !this.session) return;

    try {
      localStorage.setItem(
        'trashlab_analytics_session',
        JSON.stringify(this.session),
      );
    } catch (error) {
      this.log('Failed to save session to localStorage', error);
    }
  }

  /**
   * Load session from localStorage
   */
  private loadSession(): SessionData | null {
    if (this.config.disableCookies) return null;

    try {
      const sessionData = localStorage.getItem('trashlab_analytics_session');
      return sessionData ? JSON.parse(sessionData) : null;
    } catch (error) {
      this.log('Failed to load session from localStorage', error);
      return null;
    }
  }

  /**
   * Generate a unique ID
   */
  private generateId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }

  /**
   * Set up event listeners for tracking user interactions
   */
  private setupEventListeners() {
    // Track clicks on links and buttons
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;

      // Check if the click was on a link
      if (target.tagName === 'A' || target.closest('a')) {
        const link = target.tagName === 'A' ? target : target.closest('a');
        this.trackClick(link as HTMLAnchorElement);
      }

      // Check if the click was on a button
      if (target.tagName === 'BUTTON' || target.closest('button')) {
        const button =
          target.tagName === 'BUTTON' ? target : target.closest('button');
        this.trackClick(button as HTMLButtonElement);
      }
    });

    // Track form submissions
    document.addEventListener('submit', (event) => {
      const form = event.target as HTMLFormElement;
      this.trackFormSubmission(form);
    });

    // Track form field changes
    document.addEventListener('change', (event) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'SELECT' ||
        target.tagName === 'TEXTAREA'
      ) {
        const form = target.closest('form');
        if (form) {
          this.trackFormFieldChange(form, target as HTMLInputElement);
        }
      }
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Flush the queue immediately when the user is leaving the page
        this.flushQueue(true);
      }
    });

    // Track before unload to catch page navigations
    window.addEventListener('beforeunload', () => {
      // Flush the queue synchronously
      this.flushQueue(true);
    });
  }

  /**
   * Start the queue processing timer
   */
  private startQueueTimer() {
    this.timer = setInterval(() => {
      this.flushQueue();
    }, this.flushInterval);
  }

  /**
   * Process the event queue
   */
  private async flushQueue(sync = false) {
    if (this.isProcessingQueue || this.eventQueue.length === 0) return;

    this.isProcessingQueue = true;
    const events = [...this.eventQueue];
    this.eventQueue = [];

    try {
      // Process each event individually
      for (const event of events) {
        if (sync) {
          // Use a synchronous request for beforeunload events
          const xhr = new XMLHttpRequest();
          xhr.open(
            'POST',
            `${this.config.apiBaseUrl}/api/trpc/website.mutations.trackAnalyticsEvent`,
            false,
          );
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(
            JSON.stringify({
              json: event,
            }),
          );
        } else {
          // Use vanillaTrpc for normal async processing
          const result = await vanillaTrackAnalyticsEventMutation({
            ...event,
            metadata: event.metadata || null,
          });

          // Update session with new IDs if provided
          if (result?.sessionId && this.session) {
            this.session.sessionId = result.sessionId;
            this.session.visitorId = result.visitorId;
            this.saveSession();
          }
        }
      }
    } catch (error) {
      this.log('Failed to process event queue', error);
      this.log('Events that failed:', events);
      // Put the events back in the queue to retry later
      this.eventQueue = [...events, ...this.eventQueue];
    } finally {
      this.isProcessingQueue = false;
    }
  }

  /**
   * Track a page view
   */
  public trackPageView() {
    // Skip excluded paths
    if (this.shouldExcludePath(window.location.pathname)) {
      return;
    }

    // Update session data
    if (this.session) {
      this.session.lastActivity = DateTime.now().toMillis();
      if (!this.session.pagesViewed.includes(window.location.pathname)) {
        this.session.pagesViewed.push(window.location.pathname);
      }
      this.saveSession();
    }

    // Track the page view
    this.trackEvent({
      websiteId: this.config.websiteId,
      eventType: 'pageview',
      path: window.location.pathname,
      referrer: document.referrer || null,
      userAgent: navigator.userAgent,
      visitorId: this.session?.visitorId || this.generateId(),
      sessionId: this.session?.sessionId || this.generateId(),
      timestamp: DateTime.now(),
      metadata: null,
    });
  }

  /**
   * Track a click event
   */
  private trackClick(element: HTMLElement) {
    // Get element identifier
    const elementId = element.id || '';
    const elementClasses = Array.from(element.classList).join(' ');
    const elementText = element.textContent?.trim().substring(0, 50) || '';
    const elementHref = (element as HTMLAnchorElement).href || '';

    this.trackEvent({
      websiteId: this.config.websiteId,
      eventType: 'click',
      path: window.location.pathname,
      referrer: null,
      userAgent: navigator.userAgent,
      visitorId: this.session?.visitorId || this.generateId(),
      sessionId: this.session?.sessionId || this.generateId(),
      timestamp: DateTime.now(),
      metadata: {
        elementType: element.tagName.toLowerCase(),
        elementId,
        elementClasses,
        elementText,
        elementHref,
      },
    });
  }

  /**
   * Track a form field change
   */
  private trackFormFieldChange(form: HTMLFormElement, field: HTMLInputElement) {
    const formId = form.id || '';
    const fieldName = field.name || field.id || '';
    const fieldType = field.type || 'text';
    const fieldValue = field.value;

    // Store the form field data in session storage
    const formDataKey = `form_data_${this.session?.sessionId}_${formId}`;
    const existingData = sessionStorage.getItem(formDataKey);
    const formData = existingData ? JSON.parse(existingData) : {};

    // Only update if the value has changed
    const previousValue = formData[fieldName]?.value;

    if (fieldValue !== previousValue) {
      formData[fieldName] = {
        type: fieldType,
        value: fieldValue,
        hasValue: !!fieldValue,
        lastUpdated: DateTime.now().toISO(),
      };

      sessionStorage.setItem(formDataKey, JSON.stringify(formData));

      // Track the form field change event
      this.trackEvent({
        websiteId: this.config.websiteId,
        eventType: 'form_field_change',
        path: window.location.pathname,
        referrer: null,
        userAgent: navigator.userAgent,
        visitorId: this.session?.visitorId || this.generateId(),
        sessionId: this.session?.sessionId || this.generateId(),
        timestamp: DateTime.now(),
        metadata: {
          formId,
          fieldName,
          fieldType,
          hasValue: !!fieldValue,
          value: fieldValue,
          formData: formData as Record<string, any>,
        },
      });

      // Log the change for debugging
      this.log('Form field changed', {
        formId,
        fieldName,
        fieldType,
        hasValue: !!fieldValue,
        value: fieldValue,
      });
    }
  }

  /**
   * Track a form submission
   */
  private trackFormSubmission(form: HTMLFormElement) {
    // Get form identifier
    const formId = form.id || '';
    const formAction = form.action || '';
    const formMethod = form.method || 'get';

    // Get the stored form data
    const formDataKey = `form_data_${this.session?.sessionId}_${formId}`;
    const formData = sessionStorage.getItem(formDataKey);
    const parsedFormData = formData ? JSON.parse(formData) : {};

    // Extract form field data
    const formFields = Array.from(form.elements)
      .map((element) => {
        // Skip buttons and non-input elements
        if (
          element.tagName !== 'INPUT' &&
          element.tagName !== 'SELECT' &&
          element.tagName !== 'TEXTAREA'
        ) {
          return null;
        }

        // Skip submit buttons
        if (
          (element as HTMLInputElement).type === 'submit' ||
          (element as HTMLInputElement).type === 'button' ||
          (element as HTMLInputElement).type === 'reset'
        ) {
          return null;
        }

        const fieldName =
          (element as HTMLInputElement).name ||
          (element as HTMLInputElement).id ||
          '';
        const fieldData = parsedFormData[fieldName];
        const fieldValue = (element as HTMLInputElement).value;

        return {
          name: fieldName,
          type: (element as HTMLInputElement).type || '',
          value: fieldValue,
          hasValue: !!fieldValue,
          lastUpdated: fieldData?.lastUpdated,
        };
      })
      .filter(Boolean);

    // Clear the stored form data
    sessionStorage.removeItem(formDataKey);

    // Calculate completion time if we have field data
    const completionTime =
      formFields.length > 0 && formFields[0]
        ? DateTime.now()
            .diff(DateTime.fromISO(formFields[0].lastUpdated))
            .as('seconds')
        : null;

    // Log the form submission for debugging
    this.log('Form submitted', {
      formId,
      formAction,
      formMethod,
      formFields,
      completionTime,
    });

    this.trackEvent({
      websiteId: this.config.websiteId,
      eventType: 'form_submission',
      path: window.location.pathname,
      referrer: null,
      userAgent: navigator.userAgent,
      visitorId: this.session?.visitorId || this.generateId(),
      sessionId: this.session?.sessionId || this.generateId(),
      timestamp: DateTime.now(),
      metadata: {
        formId,
        formAction,
        formMethod,
        formFields,
        completionTime,
      },
    });

    // Also track this as a conversion
    this.trackConversion('form_submission', { formId });
  }

  /**
   * Track a conversion
   */
  public trackConversion(
    conversionType: string,
    metadata?: Record<string, any>,
  ) {
    this.trackEvent({
      websiteId: this.config.websiteId,
      eventType: 'conversion',
      path: window.location.pathname,
      referrer: null,
      userAgent: navigator.userAgent,
      visitorId: this.session?.visitorId || this.generateId(),
      sessionId: this.session?.sessionId || this.generateId(),
      timestamp: DateTime.now(),
      metadata: {
        conversionType,
        ...(metadata || {}),
      },
    });
  }

  /**
   * Add an event to the queue
   */
  private async trackEvent(event: TrackingEvent) {
    // Skip tracking if in excluded path
    if (this.shouldExcludePath(event.path)) {
      return;
    }

    // Get location info
    const locationInfo = await this.getLocationInfo();

    // Ensure all required fields are present and properly formatted
    const eventWithSession = {
      websiteId: this.config.websiteId,
      eventType: event.eventType,
      path: event.path,
      referrer: event.referrer || null, // Ensure referrer is null if undefined
      userAgent: event.userAgent,
      visitorId: this.session?.visitorId || this.generateId(),
      sessionId: this.session?.sessionId || this.generateId(),
      timestamp: DateTime.now(),
      ...locationInfo,
      metadata:
        event.metadata !== undefined
          ? event.metadata
          : {
              sessionId: this.session?.sessionId,
              visitorId: this.session?.visitorId,
            },
    };

    this.eventQueue.push(eventWithSession);
    this.log('Event added to queue', eventWithSession);

    // Flush the queue immediately if it's getting too large
    if (this.eventQueue.length >= 10) {
      this.flushQueue();
    }
  }

  /**
   * Check if a path should be excluded from tracking
   */
  private shouldExcludePath(path: string): boolean {
    if (!this.config.excludePaths || this.config.excludePaths.length === 0) {
      return false;
    }

    return this.config.excludePaths.some((excludedPath) => {
      if (excludedPath.endsWith('*')) {
        // Wildcard match for path prefix
        const prefix = excludedPath.slice(0, -1);
        return path.startsWith(prefix);
      }
      return path === excludedPath;
    });
  }

  /**
   * Log debug messages
   */
  private log(message: string, data?: any) {
    if (this.config.debug) {
      console.log(`[TrashLab Analytics] ${message}`, data || '');
    }
  }

  /**
   * Clean up resources when the tracker is no longer needed
   */
  public destroy() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    // Flush any remaining events
    this.flushQueue(true);
  }

  private async getLocationInfo(): Promise<{
    country?: string;
    region?: string;
    city?: string;
  }> {
    try {
      // Use ipapi.co which supports HTTPS (required for mixed content policy on HTTPS sites)
      // The free tier allows up to 1000 requests/day with a 30 requests/minute limit
      const response = await fetch('https://ipapi.co/json/', {
        mode: 'cors',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch location info');
      }

      const data = await response.json();

      // ipapi.co uses different field names than ip-api.com
      return {
        country: data.country_name,
        region: data.region,
        city: data.city,
      };
    } catch (error) {
      this.log('Failed to fetch location info', error);
      // Return empty object instead of throwing to prevent tracking failures
      return {};
    }
  }
}

/**
 * Initialize the analytics tracker
 */
export function initAnalytics(config: TrackerConfig): TrashLabAnalytics {
  return new TrashLabAnalytics(config);
}
